/* eslint-disable react/jsx-pascal-case */
import { lazy } from "react";
import { Navigate } from "react-router-dom";

import { getUserInfo } from "../storage";
import CheckPrice from "../jobs/checkPrice/CheckPrice";
import Tokenize from "../cardTokenizing/CardTokenizing";

// Lazy-loaded components
const SignIn = lazy(() => import("../auth/signIn/SignIn"));
const SignUp = lazy(() => import("../auth/signUp/SignUp"));
const Lockscreen = lazy(() => import("../auth/lockscreen"));
const ForgotPassword = lazy(() => import("../auth/forgotPassword"));
const EmailVerification = lazy(() => import("../auth/emailVerification"));
const CreateJobs = lazy(() => import("../jobs/createJobs"));
const CurrentJob = lazy(() => import("../jobs/currentJob"));
const AllJobs = lazy(() => import("../jobs/all"));
const AllJobsList = lazy(() => import("../jobs/list/List"));
const JobDetails = lazy(() => import("../jobs/details/JobDetails"));
const DisputesList = lazy(() => import("../jobs/disputes/list/Disputes"));
const FtmRequestList = lazy(() => import("../jobs/ftmRequest/list/List"));
const SearchResults = lazy(() => import("../jobs/searchResults"));
const Home = lazy(() => import("../home"));
const Wallet = lazy(() => import("../customer/wallet"));
const Notifications = lazy(() => import("../notifications"));
const _404Error = lazy(() => import("../error/404/_404Error"));
const Profile = lazy(() => import("../customer/profile/Profile"));
const PaymentsSummary = lazy(() => import("../payments/summary/Summary"));
const FutureJobsCalendar = lazy(() => import("../jobs/futureJobs/calendar/FutureJobsCalendar"));
const FutureJobsList = lazy(() => import("../jobs/futureJobs/list/List"));
const OfficeMessages = lazy(() => import("../messages/office"));
const DisputesMessages = lazy(() => import("../messages/disputes"));
const JobsMessages = lazy(() => import("../messages/jobs"));
const QrCodes = lazy(() => import("../auth/qrCodes/QrCodes"));
const MessagesText = lazy(() => import("../messages/text/Text"));
const CreditNoteList = lazy(() => import("../customer/creditNote/List"));

// Mock authentication function
const isAuthenticated = () => {
    const userInfo = getUserInfo();
    return userInfo ? true : false;
};

// Higher-order component for protected routes
const ProtectedRoute = ({ element }) => {
    return isAuthenticated() ? element : <Navigate to="/sign/in" />;
};

// Routes Configuration
export const routes = [
    // Public Routes
    { path: "/", element: <SignIn />, page_name: "Sign In" },
    { path: "/sign/in", element: <SignIn />, page_name: "Sign In" },
    { path: "/sign/in/:type", element: <SignIn />, page_name: "Sign In" },
    { path: "/sign/up", element: <SignUp />, page_name: "Sign Up" },
    { path: "/sign/up/:key", element: <SignUp />, page_name: "Sign Up" },
    { path: "/lockscreen", element: <Lockscreen />, page_name: "Lockscreen" },
    { path: "/forgot/password/:customer_id", element: <ForgotPassword />, page_name: "Forgot Password" },
    { path: "/email/verification/:customer_id", element: <EmailVerification />, page_name: "Email Verification" },
    { path: "/qr/code", element: <QrCodes />, page_name: "Continue mobile app" },
    { path: "/check/price", element: <CheckPrice isHome={false} isCheckingPrice={true} />, page_name: "Check Price" },
    { path: "/tokenize", element: <Tokenize/>, page_name: "Tokenize" },

    // Protected Routes
    { path: "/create/jobs", element: <ProtectedRoute element={<CreateJobs />} />, page_name: "Create Jobs" },
    { path: "/current/job/:job_id", element: <ProtectedRoute element={<CurrentJob />} />, page_name: "Current Job" },
    { path: "/jobs/all", element: <ProtectedRoute element={<AllJobs />} />, page_name: "All Jobs" },
    { path: "/jobs/list", element: <ProtectedRoute element={<AllJobsList />} />, page_name: "Jobs" },
    { path: "/jobs/details/:job_id", element: <ProtectedRoute element={<JobDetails />} />, page_name: "Job Details" },
    { path: "/jobs/disputes/list", element: <ProtectedRoute element={<DisputesList />} />, page_name: "Disputes List" },
    { path: "/ftm/request/list", element: <ProtectedRoute element={<FtmRequestList />} />, page_name: "Tradespersons Request List" },
    { path: "/job/search/results", element: <ProtectedRoute element={<SearchResults />} />, page_name: "Search Results" },
    { path: "/home", element: <ProtectedRoute element={<Home />} />, page_name: "Home" },
    { path: "/profile", element: <ProtectedRoute element={<Profile />} />, page_name: "Profile" },
    { path: "/wallet", element: <ProtectedRoute element={<Wallet />} />, page_name: "Wallet" },
    { path: "/payments", element: <ProtectedRoute element={<PaymentsSummary />} />, page_name: "Payments" },
    { path: "/notifications", element: <ProtectedRoute element={<Notifications />} />, page_name: "Notifications" },
    { path: "/future/jobs/calendar", element: <ProtectedRoute element={<FutureJobsCalendar />} />, page_name: "Future Jobs" },
    { path: "/future/jobs/list", element: <ProtectedRoute element={<FutureJobsList />} />, page_name: "Future Jobs" },
    { path: "/office/messages", element: <ProtectedRoute element={<OfficeMessages />} />, page_name: "Office Messages" },
    { path: "/disputes/messages", element: <ProtectedRoute element={<DisputesMessages />} />, page_name: "Disputes Messages" },
    { path: "/jobs/messages", element: <ProtectedRoute element={<JobsMessages />} />, page_name: "Jobs Messages" },
    { path: "/text/messages", element: <ProtectedRoute element={<MessagesText />} />, page_name: "Text Messages" },
    { path: "/credit/note/list", element: <ProtectedRoute element={<CreditNoteList />} />, page_name: "Credit Note List" },

    // Catch-All Route (404)
    { path: "/*", element: <_404Error />, page_name: "404 Error" },
];